/**
 * ReviewPage - Submit review after event completion
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Star, ThumbsUp, Camera, Loader2, Check, ArrowLeft, Building
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating component
function StarRating({ value, onChange, label, size = 'md' }) {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';

  return (
    <div>
      {label && <label className="text-sm font-medium text-gray-700 block mb-2">{label}</label>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(star)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={`${sizeClass} ${
                star <= (hovered || value)
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReviewPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [serviceRating, setServiceRating] = useState(0);
  const [valueRating, setValueRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [reviewerName, setReviewerName] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if review already exists
        const reviewRes = await fetch(`${API_URL}/api/reviews/booking/${bookingId}`);
        if (reviewRes.ok) {
          const reviewData = await reviewRes.json();
          if (reviewData.review) {
            setSubmitted(true);
            return;
          }
        }

        // Get booking details
        const bookingsRes = await fetch(`${API_URL}/api/events/bookings?booking_id=${bookingId}`);
        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          if (bookingsData.bookings?.length > 0) {
            const bookingData = bookingsData.bookings[0];
            setBooking(bookingData);
            setReviewerName(bookingData.contact?.name || '');

            // Get provider details
            const providerRes = await fetch(`${API_URL}/api/events/providers/${bookingData.provider_id}`);
            if (providerRes.ok) {
              const providerData = await providerRes.json();
              setProvider(providerData);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bookingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select an overall rating');
      return;
    }

    setSubmitting(true);
    try {
      const reviewData = {
        booking_id: bookingId,
        provider_id: booking.provider_id,
        rating,
        review_text: reviewText,
        service_rating: serviceRating || null,
        value_rating: valueRating || null,
        communication_rating: communicationRating || null,
        would_recommend: wouldRecommend,
        is_anonymous: isAnonymous,
        reviewer_name: isAnonymous ? null : reviewerName,
        reviewer_email: booking.contact?.email
      };

      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });

      if (response.ok) {
        toast.success('Thank you for your review!');
        setSubmitted(true);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h1>
            <p className="text-gray-600 mb-6">
              Your review has been submitted. It helps other customers make better decisions!
            </p>
            <Button onClick={() => navigate('/events')}>
              Back to Events
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex items-center gap-3">
            <Building className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Leave a Review</h1>
              <p className="text-amber-100">{provider?.provider_name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle>How was your experience?</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Overall Rating */}
              <div className="text-center p-6 bg-amber-50 rounded-lg">
                <p className="text-lg font-medium text-gray-700 mb-4">Overall Rating</p>
                <div className="flex justify-center">
                  <StarRating value={rating} onChange={setRating} size="lg" />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {rating === 0 ? 'Tap a star to rate' :
                   rating <= 2 ? 'We\'re sorry to hear that' :
                   rating === 3 ? 'Thanks for the feedback' :
                   rating === 4 ? 'Great to hear!' : 'Excellent!'}
                </p>
              </div>

              {/* Detailed Ratings */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <StarRating value={serviceRating} onChange={setServiceRating} />
                  <p className="text-xs text-gray-500 mt-2">Service Quality</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <StarRating value={valueRating} onChange={setValueRating} />
                  <p className="text-xs text-gray-500 mt-2">Value for Money</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <StarRating value={communicationRating} onChange={setCommunicationRating} />
                  <p className="text-xs text-gray-500 mt-2">Communication</p>
                </div>
              </div>

              {/* Written Review */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Tell us more about your experience
                </label>
                <Textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="What did you like? What could be improved?"
                  rows={5}
                />
              </div>

              {/* Would Recommend */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <ThumbsUp className={`w-6 h-6 ${wouldRecommend ? 'text-green-600' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <p className="font-medium">Would you recommend this provider?</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={wouldRecommend ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setWouldRecommend(true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={!wouldRecommend ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setWouldRecommend(false)}
                  >
                    No
                  </Button>
                </div>
              </div>

              {/* Reviewer Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="anonymous"
                    checked={isAnonymous}
                    onCheckedChange={setIsAnonymous}
                  />
                  <Label htmlFor="anonymous">Post anonymously</Label>
                </div>
                
                {!isAnonymous && (
                  <Input
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    placeholder="Your name (will be displayed publicly)"
                  />
                )}
              </div>

              <Button
                type="submit"
                disabled={submitting || rating === 0}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit Review
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
